import { v4 as uuidv4 } from 'uuid';
import { computed, inject, toRaw } from 'vue';
import { useRoute } from 'vue-router';
import { useRouter } from 'vue-router/dist/vue-router';
import { useStore } from 'vuex';

import useAuthoring from '~/composables/useAuthoring';
import useDocumentEditor from '~/composables/useDocumentEditor';
import useFormFilters from '~/composables/useFormFilters';
import useQuickPrompts from '~/composables/useQuickPrompts';
import useSecondOrderReferencesGating from '~/composables/useSecondOrderReferencesGating';

import {
  CreateNewConversation,
  PopMessage,
  SetConversationDefaultState,
  SetConversationScope,
  SetCurrentConversationName,
  SetIsNewConversation,
} from '~/constants/actions';
import { DEFAULT_COLLECTION, INITIAL_CONVERSATION, METADATA, queryType, RESPONSE_TYPE } from '~/constants/conversationModels';
import { DOCUMENT_FILTER_FIELDS_AI, FILTER_TYPE, TRANSLATION_TYPE } from '~/constants/documents';
import {
  GetConversationScope,
  GetCurrentConversation,
  GetIsNewConversation,
  GetIsTyping,
  GetUserConversations,
  GetUserData,
} from '~/constants/getters';
import { AddChunk, AddMessage, SetCurrentConversation, SetInitialConversation } from '~/constants/mutations';
import { Routes } from '~/constants/routes';
import { TACTIC_TYPES } from '~/constants/tacticTypes';

import { fetchConversationById, gotoConversationSSE, sendMessageSSE } from '~/services/api/conversations';
import { apiGatewayEndpoints } from '~/services/arcane-api-gateway/api-gateway-endpoints';

import { encodeToBase64, renderPromptCardText } from '~/utils/message';
import { processCitedReferences } from '~/utils/citation-messages';
import { buildAssistQuery } from '~/utils/new-agent-utils';
import { buildMessagesFromTurns } from '~/utils/new-agent-messages';
import useAssistAgent from './useAssistAgent';
import useImageGeneration from './useImageGeneration';

export default () => {
  const arcaneApiGateway = inject('arcaneApiGateway');
  const conversationSubscriptionService = inject('conversationSubscriptionService');

  const store = useStore();
  const router = useRouter();
  const route = useRoute();

  const {
    updateAuthoredDocumentResource,
    subscribeLayoutGeneration,
    unsubscribeLayoutGeneration,
    currentTemplate,
    resetLayoutIndex,
    mapAuthoringLayoutDataToSend,
  } = useDocumentEditor();
  const { isAuthoringStarted, tacticContextFilters, setFootnotes } = useAuthoring();
  const { formFilters } = useFormFilters();
  const { isSecondOrderReferencesOrg } = useSecondOrderReferencesGating();
  const { currentFields, currentPromptCard, setCurrentPromptCard } = useQuickPrompts();
  const { remixImage, setUserImageForRemix } = useImageGeneration();
  const { streamConversationResponse, resumeConversation, changeImageStyle } = useAssistAgent();

  const userConversations = computed(() => store.getters[`conversationStore/${GetUserConversations}`]);

  const currentConversation = computed(() => store.getters[`conversationStore/${GetCurrentConversation}`]);

  const conversationScope = computed(() => store.getters[`conversationStore/${GetConversationScope}`]);

  const isTyping = computed(() => store.getters[`conversationStore/${GetIsTyping}`]);

  //  const conversationFilters = computed(() => store.getters[`conversationStore/${GetConversationFilters}`]);

  const isNewConversation = computed(() => store.getters[`conversationStore/${GetIsNewConversation}`]);

  const isFilterFormDisabled = computed(
    () => conversationScope.value.isScoped && route.name === Routes.Conversation.name,
  );

  const userData = computed(() => store.getters[`authStore/${GetUserData}`]);

  const display2ndOrderReferences = computed(
    () => route.name === Routes.Conversation.name && isSecondOrderReferencesOrg.value,
  );

  const useNewAgent = computed(() => route.name !== Routes.Authoring.name); // use newAgent for Assist, on every org
  const setConversationScope = (data) => store.dispatch(`conversationStore/${SetConversationScope}`, data);

  const setIsNewConversation = (data) => store.dispatch(`conversationStore/${SetIsNewConversation}`, data);

  // const setConversationFilters = (data) => store.dispatch(`conversationStore/${SetConversationFilters}`, data);
  // const setInitialFilters = () => store.dispatch(`conversationStore/${SetInitialFilters}`);
  const setDefaultState = () => store.dispatch(`conversationStore/${SetConversationDefaultState}`);

  const resetCurrentConversation = () => store.dispatch(`conversationStore/${SetInitialConversation}`);

  const onPopMessage = (messageId) => store.dispatch(`conversationStore/${PopMessage}`, messageId);

  // we need this because our publish method will publish on different channel based on the current page
  const getCurrentChannelNameToPublish = (conversationId, currentRouteName = route.name) => {
    return currentRouteName === Routes.Authoring.name
      ? `content-authoring:${conversationId}`
      : `conversations-query:${conversationId}`;
  };

  const postLog = async (correlationId, message) =>
    arcaneApiGateway.post(apiGatewayEndpoints.logger.putLog, {
      correlationId,
      message,
      entity: 'Frontend',
      timestamp: Date.now(),
    });

  let waitingForMessage = false;

  const conversationCallback = async (message) => {
    const data = JSON.parse(message.data);
    const { correlationId, responseType } = data;
    console.log('Data received-->', responseType, data);

    console.log('Received data-->', data);

    switch (responseType) {
      case RESPONSE_TYPE.layoutGeneration: {
        //case handled in useDocumentEditor
        break;
      }
      case RESPONSE_TYPE.systemMessages:
      case RESPONSE_TYPE.endMessage:
      case RESPONSE_TYPE.data:
      case RESPONSE_TYPE.error:
      case RESPONSE_TYPE.messages: {
        // Ably only ever carries these for old-agent conversations — new-agent turns get their
        // main response over the SSE stream instead (see streamConversationResponse) and never
        // publish endMessage/data/error/messages over Ably, so no useNewAgent gate is needed here.
        store.dispatch(`conversationStore/${AddChunk}`, data);
        break;
      }
      default:
        console.log('[ERROR]', data);
        console.log('Invalid type of message');
        break;
    }

    if (waitingForMessage) {
      await postLog(correlationId, `Received message: ${JSON.stringify(data)}`);
      waitingForMessage = false;
    }
  };

  const namingConversationCallback = async (message) => {
    const data = JSON.parse(message.data);
    if (data && data.data && data.data[0]) {
      store.dispatch(`conversationStore/${SetCurrentConversationName}`, data.data[0]);
    }
  };

  /**
   * Subscribes to the response channel of the given conversation. Always subscribes
   * regardless of agent — new-agent Assist conversations still need this for
   * RESPONSE_TYPE.systemMessages; conversationCallback filters out what the SSE stream
   * already delivers.
   * @param {string} conversationId - UUID of the conversation.
   * @param {string} routeName - current Route
   */
  const subscribeConversation = (conversationId, routeName) => {
    conversationSubscriptionService.subscribe(conversationId, routeName, conversationCallback);

    if (routeName === Routes.Authoring.name) {
      subscribeLayoutGeneration(conversationId);
    }
  };

  /**
   * Unsubscribes from the response channel of the given conversation.
   * @param {string} conversationId - UUID of the conversation.
   * @param {string} unsubscribeFromRoute - current route
   */
  const unsubscribeConversation = (conversationId, unsubscribeFromRoute) => {
    conversationSubscriptionService.unsubscribe(conversationId, unsubscribeFromRoute);

    if (unsubscribeFromRoute === Routes.Authoring.name) {
      unsubscribeLayoutGeneration(conversationId);
    }
  };

  /**
   * Subscribes to the namimg channel of the given conversation.
   * @param {string} conversationId - UUID of the conversation.
   */
  const subscribeToConversationNamingChannel = () => {
    conversationSubscriptionService.subscribeNaming(namingConversationCallback);
  };

  /**
   * Unsubscribes from the naming channel of the given conversation.
   * @param {string} conversationId - UUID of the conversation.
   */
  const unsubscribeFromConversationNamingChannel = () => {
    conversationSubscriptionService.unsubscribeNaming();
  };

  /**
   * Creates a new conversation, subscribes to the response channel and routes to the correct url.
   */
  const createNewConversation = async () => {
    const convId = uuidv4();

    const userId = userData.value?.id;
    // Is this necessary if setCurrentConversation also subscribes?
    subscribeConversation(convId, Routes.Conversation.name);
    await router.push({ name: Routes.Conversation.name, params: { conversationId: `${convId}` } });
    store.dispatch(`conversationStore/${CreateNewConversation}`, { conversationId: convId, userId });
  };

  const initialiseConversation = () => {
    store.dispatch(`conversationStore/${SetInitialConversation}`);
    router.push({ name: Routes.Conversation.name });
  };

  /**
   * Creates a new authoring conversation, subscribes to the response channel and routes to the correct url.
   */
  const createAuthoringConversation = async (content, conversationId = '', footnotes = []) => {
    const convId = conversationId || uuidv4();
    setFootnotes(footnotes);

    // Is this necessary if setCurrentConversation also subscribes?
    subscribeConversation(convId, Routes.Authoring.name);
    await router.push({ name: Routes.Authoring.name, params: { conversationId: `${convId}` } });
    await store.dispatch(`conversationStore/${CreateNewConversation}`, {
      conversationId: convId,
      isAuthoring: true,
    });
    await publishToConversation(convId, queryType.authoringQuery, '');
    await updateAuthoredDocumentResource(convId, { content, tacticType: TACTIC_TYPES.email }, false);
  };

  const getConversationById = async (conversationId) => {
    try {
      const conversation = await fetchConversationById(conversationId, useNewAgent.value);
      return conversation?.data;
    } catch (error) {
      console.error('Error fetching conversation by id:', error);
      return null;
    }
  };

  /**
   * Legacy (flat, pre-turns/events) message transform — still live for both Authoring (which
   * never gets turns/events) and any Assist conversation not yet backfilled into the new schema
   * (see turn-schema-migration-plan.md). Extracted from setCurrentConversation purely to keep
   * that function readable; the branch it's called from still decides per-conversation shape.
   * @param {object[]} messages - `conversation.messages`, flat legacy shape.
   */
  const buildLegacyMessages = (messages) =>
    messages
      .sort((message1, message2) => message1.timestamp - message2.timestamp)
      .map((message) => {
        // Process citedReferences if message has data blocks
        let processedData = message.data || [];

        if (message.data && message.data.length > 0) {
          const documentListBlock = message.data.find((block) => block.type === 'documentList');
          if (documentListBlock) {
            processedData = processCitedReferences(message.message, message.data, false);
          }
        }

        return {
          ...message,
          ...(message.collection_id ? message.collection_id : DEFAULT_COLLECTION),
          data: processedData,
        };
      });

  /**
   * Sets the current conversation in the store and subscribes to the response channel.
   * @param {string} conversationId - UUID of the conversation.
   */
  const setCurrentConversation = async (conversationId) => {
    try {
      store.dispatch(`conversationStore/${SetCurrentConversation}`, INITIAL_CONVERSATION);
      const conversation = await getConversationById(conversationId);
      if (conversation) {
        const isTurnsShape = route.name !== Routes.Authoring.name && Boolean(conversation.turns);
        const sortedMessages = isTurnsShape
          ? buildMessagesFromTurns(conversation.turns)
          : buildLegacyMessages(conversation.messages);
        const conversationWithSortedMessages = {
          ...conversation,
          conversationId: conversation.conversationId || conversation.id,
          messages: sortedMessages,
        };
        // here we will call groupMessagesByScopedCollection
        store.dispatch(`conversationStore/${SetCurrentConversation}`, conversationWithSortedMessages);

        return conversationWithSortedMessages;
      }
      return conversation;
    } catch (error) {
      console.error('Error fetching conversation by id:', error);
      return null;
    }
  };

  const processFilters = (filters, type = FILTER_TYPE.document) => {
    if (!filters[type]) return [];

    return Object.entries(filters[type]).map(([key, value]) => ({
      key,
      value: Array.isArray(value) ? [...value] : [value],
    }));
  };

  const mapCollectionData = (collection) => {
    const filters = [];
    if (collection && Object.keys(collection).length) {
      const documents = collection.documents?.map((document) => document.externalId || document);
      filters.push({ key: DOCUMENT_FILTER_FIELDS_AI.externalId, value: documents });
      const processedCollection = {
        collectionId: collection.collectionId,
        collectionName: encodeToBase64(collection.collectionName),
        documentCount: documents?.length || collection?.documentCount || 0,
      };
      return { externalIdsFilters: filters, processedCollection };
    }
    return { externalIdsFilters: filters };
  };

  const processPromptCardData = (currentFields, currentPromptCard) => {
    if (currentPromptCard.value?.id) {
      return {
        ...(currentFields.value?.length && { promptCardVariables: currentFields.value.map((item) => toRaw(item)) }),
        ...(!!currentPromptCard.value?.id && { versionContentId: currentPromptCard.value?.id }),
        ...(!!currentPromptCard.value?.title && { promptCardTitle: currentPromptCard.value?.title }),
      };
    }
    return {};
  };

  const getFilters = (metaFilters, externalIdsFilters) => {
    const filters = [...metaFilters, ...externalIdsFilters];
    // Remove empty arrays, arrays with only empty strings, empty strings and falsy values like null/undefined
    const filteredValues = filters.filter(({ value }) =>
      Array.isArray(value) ? value.some((v) => v !== '') : value !== '' && value != null,
    );

    return filteredValues;
  };

  const processAuthoringDataAndFilters = (data, collection) => {
    const metaFilters = processFilters(formFilters.value);
    // if data is empty, this mean we publish automatically only the collection to llm (authoring case) and we need empty layouts
    const { layouts = [], selected_layout = null } =
      data && isAuthoringStarted.value ? mapAuthoringLayoutDataToSend() : {};
    const layoutsData = {
      designSystemId: currentTemplate.value?.parentId || currentConversation.value?.designSystemId,
      templateId: currentTemplate.value?.id || currentConversation.value?.templateId,
      layouts,
      ...(selected_layout !== null && { selected_layout }),
    };
    const tacticContexts = Object.keys(tacticContextFilters.value)?.map((key) => tacticContextFilters.value[key].id);

    const { externalIdsFilters, processedCollection } = mapCollectionData(collection);

    // For authoring: send only externalId under `filters`,
    // and send product/country under `media_filters`.
    const media_filters = metaFilters.filter(({ key }) => key === METADATA.product_name || key === METADATA.country);
    const filters = getFilters([], externalIdsFilters);

    return {
      filters,
      media_filters,
      ...layoutsData,
      tacticContexts,
      collection: processedCollection,
    };
  };

  const processTextMessageAsPromptCardText = () =>
    renderPromptCardText(currentPromptCard.value.prompt, currentFields.value || []);

  /**
   * Publishes an authoring turn over the legacy Ably channel. Authoring stays on the
   * old agent for the foreseeable future — there's no new-agent branch here, unlike
   * publishAssistMessage.
   */
  const publishAuthoringMessage = async ({
    conversationId,
    queryType,
    textMessage,
    collectionWithCorrectIdIfShared,
    correlationId,
    responseMessageId,
  }) => {
    // Old-agent-only diagnostic: tracks Ably round-trip time, consumed by conversationCallback.
    waitingForMessage = true;
    await postLog(correlationId, `Publishing query: ${JSON.stringify(textMessage)}`);

    await resetLayoutIndex();

    //need this conversion to seconds for python compatibility timestamps
    const timestamp = Math.floor(Date.now() / 1000);
    const payload = {
      correlationId,
      responseMessageId,
      message_type: queryType,
      query: encodeToBase64(textMessage),
      timestamp,
      ...processAuthoringDataAndFilters(textMessage, collectionWithCorrectIdIfShared),
    };
    conversationSubscriptionService.publish(
      getCurrentChannelNameToPublish(conversationId, Routes.Authoring.name),
      payload,
    );
  };

  /**
   * Publishes an assist turn. New agent is on for every org outside Authoring now, so this only
   * ever streams: /commands/goto for a remix, or /stream otherwise.
   */
  const publishAssistMessage = async ({
    conversationId,
    textMessage,
    promptCardsData,
    filters,
    imageRegenerationParams,
    responseMessageId,
    userMessageId,
  }) => {
    if (remixImage.value?.remixImageId) {
      // Remixing already knows it's headed to image generation, so skip intent routing.
      const stream = gotoConversationSSE(conversationId, {
        goto: 'image_generation',
        query: buildAssistQuery(textMessage, promptCardsData),
        imageGenerationParams: {
          width: imageRegenerationParams.width || 1024,
          height: imageRegenerationParams.height || 1024,
          brandStyleId: imageRegenerationParams.brandStyleId ?? null,
          remixImageId: imageRegenerationParams.remixImageId,
          numberOfImages: imageRegenerationParams.numberOfImages || 4,
        },
      });
      await streamConversationResponse(stream, responseMessageId, conversationId, { userMessageId });
    } else {
      const response = await sendMessageSSE(conversationId, {
        query: buildAssistQuery(textMessage, promptCardsData),
        filters,
      });
      await streamConversationResponse(response, responseMessageId, conversationId, { userMessageId });
    }
  };

  /**
   * Sends a query to the given conversation. Authoring always publishes over the legacy
   * Ably channel (publishAuthoringMessage); Assist picks old vs new agent internally
   * (publishAssistMessage) based on useNewAgent. This function only does the prep that's
   * identical either way: message bookkeeping, prompt-card/image-gen param resolution,
   * and the local AddMessage dispatch.
   *
   * @param {string} conversationId - UUID of the conversation.
   * @param {enum} queryType - type of query sent.
   * @param {*} data - conversation data to be sent.
   */
  const publishToConversation = async (conversationId, queryType, data) => {
    const responseMessageId = uuidv4();
    const userMessageId = uuidv4();
    const correlationId = uuidv4();
    let currentCollection;
    let textMessage = data;

    const { collection } = conversationScope.value;
    if (collection && Object.keys(collection)) {
      currentCollection = {
        collectionId: collection?.parentId || collection.collectionId,
        collectionName: collection.collectionName,
        documentCount: collection.documents?.length || 0,
      };
    }
    // we are to default collection vault
    else {
      currentCollection = DEFAULT_COLLECTION;
    }
    const promptCardsData = processPromptCardData(currentFields, currentPromptCard);

    if (promptCardsData.versionContentId) {
      textMessage = processTextMessageAsPromptCardText();
    }

    const imageRegenerationParams = remixImage.value?.remixImageId
      ? {
          remixImageId: remixImage.value.remixImageId,
          width: remixImage.value.width,
          height: remixImage.value.height,
        }
      : null;

    const metaFilters = processFilters(formFilters.value);

    const { externalIdsFilters } = mapCollectionData(collection);

    const filters = getFilters(metaFilters, externalIdsFilters);

    store.dispatch(`conversationStore/${AddMessage}`, {
      data: textMessage,
      currentCollection,
      responseMessageId,
      userMessageId,
      filters,
      ...promptCardsData,
      ...(remixImage.value?.remixImageId && { remixImageId: imageRegenerationParams.remixImageId }),
    });

    const collectionWithCorrectIdIfShared = collection?.collectionId
      ? {
          ...collection,
          collectionId:
            currentCollection.collectionId !== DEFAULT_COLLECTION.collectionId // if it's not valut collection
              ? currentCollection.collectionId
              : collection.collectionId,
        }
      : collection;

    const sharedArgs = {
      conversationId,
      queryType,
      textMessage,
      collectionWithCorrectIdIfShared,
      correlationId,
      responseMessageId,
      userMessageId,
    };

    if (route.name === Routes.Authoring.name) {
      await publishAuthoringMessage(sharedArgs);
    } else {
      await publishAssistMessage({ ...sharedArgs, promptCardsData, filters, imageRegenerationParams });
    }

    setCurrentPromptCard(null);
    setUserImageForRemix(null);
  };

  const translateDocument = async (
    externalId,
    sourceLanguage,
    sourceLanguageName,
    name,
    targetLanguage,
    glossaryId = null,
    type = '',
    provider = null,
  ) => {
    if (!currentConversation.value.conversationId && type !== TRANSLATION_TYPE.email) {
      await setIsNewConversation(true);
      await createNewConversation();
    }

    try {
      const { data } = await arcaneApiGateway.post(apiGatewayEndpoints.translation.translateDocument, {
        externalId,
        languageCode: sourceLanguage,
        languageName: sourceLanguageName,
        name,
        targetLanguage,
        ...(type !== TRANSLATION_TYPE.email && { conversationId: currentConversation.value.conversationId }),
        glossaryId,
        ...(provider && { provider }),
      });
      return data;
    } catch (err) {
      console.log('Error in translating a document', err);
    }
  };

  const downloadMessageById = async ({ messageId, conversationId, conversationUserId, fileType }) => {
    try {
      const { data } = await arcaneApiGateway.post(apiGatewayEndpoints.markdown.downloadMessage, {
        messageId,
        conversationId,
        conversationUserId,
        ...(fileType && { fileType }),
      });
      if (data.downloadUrl) {
        window.open(data.downloadUrl, '_blank');
      }
      if (data.readUrl) {
        return data.readUrl;
      }
      return null;
    } catch (err) {
      console.log('Error in downloading message', err);
      return null;
    }
  };

  return {
    currentConversation,
    createAuthoringConversation,
    createNewConversation,
    conversationScope,
    initialiseConversation,
    isFilterFormDisabled,
    isNewConversation,
    isTyping,
    getConversationById,
    publishToConversation,
    resumeConversation,
    changeImageStyle,
    translateDocument,
    setCurrentConversation,
    setConversationScope,
    setDefaultState,
    resetCurrentConversation,
    setIsNewConversation,
    subscribeConversation,
    subscribeToConversationNamingChannel,
    unsubscribeConversation,
    unsubscribeFromConversationNamingChannel,
    userConversations,
    onPopMessage,
    downloadMessageById,
    useNewAgent,
    display2ndOrderReferences,
  };
};
